import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { ParsedPromptSectionData } from "src/services/experiences/promptEditor";

interface PromptEditorContextValue {
  promptData: ParsedPromptSectionData | null;
  isLoading: boolean;
  setPromptData: (data: ParsedPromptSectionData) => void;
  setLoading: (loading: boolean) => void;
  updateSectionOrder: (sections: ParsedPromptSectionData["pageStructure"]["sections"]) => void;
  reset: () => void;
}

const PromptEditorContext = createContext<PromptEditorContextValue | null>(null);

export const PromptEditorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [promptData, setPromptDataState] = useState<ParsedPromptSectionData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const setPromptData = (data: ParsedPromptSectionData) => {
    setPromptDataState(data);
    setIsLoading(false);
  };

  const setLoading = useCallback((loading: boolean) => {
    setIsLoading(loading);

    if (loading) {
      setPromptDataState(null);
    }
  }, []);

  const updateSectionOrder = useCallback(
    (
      sections: ParsedPromptSectionData["pageStructure"]["sections"]
    ) => {
      setPromptDataState((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pageStructure: {
            ...prev.pageStructure,
            sections,
          },
        };
      });
    },
    []
  );

  const reset = useCallback(() => {
    setPromptDataState(null);
    setIsLoading(false);
  }, []);

  const value = useMemo(
    () => ({
      promptData,
      isLoading,
      setPromptData,
      setLoading,
      updateSectionOrder,
      reset,
    }),
    [
      promptData,
      isLoading,
      setPromptData,
      setLoading,
      updateSectionOrder,
      reset,
    ]
  );

  return (
    <PromptEditorContext.Provider value={value}>
      {children}
    </PromptEditorContext.Provider>
  );
};

export const usePromptEditorContext = (): PromptEditorContextValue => {
  const ctx = useContext(PromptEditorContext);
  if (!ctx) throw new Error("usePromptEditorContext must be used inside PromptEditorProvider");
  return ctx;
};